import React, { useState, useEffect, useCallback, useRef } from "react";
import { FiSend, FiInbox, FiFileText, FiBarChart2, FiSettings, FiSearch, FiRefreshCw, FiPlus, FiTrash2, FiCheck, FiX, FiUsers, FiChevronDown, FiChevronUp, FiClock, FiAlertCircle, FiCheckCircle, FiMessageCircle, FiFilter, FiZap, FiBell, FiEye } from "react-icons/fi";
import toast from "react-hot-toast";
import { waBusiness } from "../services/api";
import WaMedia, { isPlaceholderBody } from "../components/WaMedia"; // v3.4.091 — مرفقات الوارد

// ═══════════════════════════════════════════════════════════════
// التواصل المؤسسي — WhatsApp Business Cloud API
// ═══════════════════════════════════════════════════════════════

const STATUS_COLORS = {
  pending: "#f59e0b",
  sent: "#3b82f6",
  delivered: "#8b5cf6",
  read: "#10b981",
  failed: "#ef4444",
};

const STATUS_LABELS = {
  pending: "قيد الإرسال",
  sent: "تم الإرسال",
  delivered: "تم التوصيل",
  read: "مقروءة",
  failed: "فشل",
};

const TRIGGER_LABELS = {
  manual: "يدوي",
  bulk: "جماعي",
  auto_reservation: "تلقائي — حجز",
  auto_payment: "تلقائي — دفعة",
  auto_project: "تلقائي — مشروع",
  webhook: "وارد",
};

const TRIGGER_EVENTS = [
  { value: "reservation_created", label: "حجز جديد" },
  { value: "payment_due", label: "موعد دفعة" },
  { value: "payment_received", label: "دفعة مستلمة" },
  { value: "project_update", label: "تحديث مشروع" },
];

// ─── Utility ───
const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("ar-SA", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const formatPhone = (p) => {
  if (!p) return "";
  const clean = p.replace(/\D/g, "");
  if (clean.startsWith("966") && clean.length === 12) {
    return `+${clean.slice(0,3)} ${clean.slice(3,5)} ${clean.slice(5,8)} ${clean.slice(8)}`;
  }
  return p;
};

export default function BusinessCommPage({ user }) {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const isAdmin = (user?.role === "admin" && !user?.impersonating);

  const tabs = [
    { key: "dashboard", label: "لوحة التحكم", icon: <FiBarChart2 /> },
    { key: "messages", label: "سجل الرسائل", icon: <FiMessageCircle /> },
    { key: "send", label: "إرسال رسالة", icon: <FiSend /> },
    { key: "bulk", label: "إرسال جماعي", icon: <FiUsers /> },
    { key: "inbox", label: "الوارد", icon: <FiInbox /> },
    { key: "templates", label: "القوالب", icon: <FiFileText /> },
    ...(isAdmin ? [{ key: "auto", label: "إشعارات تلقائية", icon: <FiBell /> }] : []),
    ...(isAdmin ? [{ key: "config", label: "الإعدادات", icon: <FiSettings /> }] : []),
  ];

  return (
    <div style={{ padding: "24px 28px", direction: "rtl", fontFamily: "'Segoe UI', Tahoma, sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <FiMessageCircle size={24} style={{ color: "#25D366" }} />
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>التواصل المؤسسي</h2>
        <span style={{ fontSize: 11, background: "#25D366", color: "#fff", padding: "2px 10px", borderRadius: 12, fontWeight: 600 }}>WhatsApp Business</span>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, flexWrap: "wrap", borderBottom: "2px solid #e5e7eb", paddingBottom: 0 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{
              display: "flex", alignItems: "center", gap: 6, padding: "8px 16px",
              border: "none", borderBottom: activeTab === t.key ? "3px solid #25D366" : "3px solid transparent",
              background: activeTab === t.key ? "#f0fdf4" : "transparent",
              color: activeTab === t.key ? "#166534" : "#6b7280",
              fontWeight: activeTab === t.key ? 700 : 500,
              cursor: "pointer", fontSize: 13, borderRadius: "8px 8px 0 0",
              transition: "all .15s",
            }}
          >
            {t.icon}<span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "dashboard" && <DashboardTab />}
      {activeTab === "messages" && <MessagesTab />}
      {activeTab === "send" && <SendTab />}
      {activeTab === "bulk" && <BulkSendTab />}
      {activeTab === "inbox" && <InboxTab />}
      {activeTab === "templates" && <TemplatesTab isAdmin={isAdmin} />}
      {activeTab === "auto" && <AutoNotificationsTab />}
      {activeTab === "config" && <ConfigTab />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Dashboard Tab — لوحة التحكم
// ═══════════════════════════════════════════════════════════════
function DashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await waBusiness.getDashboard();
      setData(res.data);
    } catch (err) {
      toast.error("خطأ في تحميل البيانات");
    }
    setLoading(false);
  };

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>جاري التحميل...</div>;
  if (!data) return <div style={{ textAlign: "center", padding: 40, color: "#ef4444" }}>خطأ في تحميل البيانات</div>;

  const s = data.stats || {};

  const statCards = [
    { label: "إجمالي الرسائل", value: s.total || 0, color: "#3b82f6", icon: <FiMessageCircle /> },
    { label: "مرسلة", value: s.sent || 0, color: "#8b5cf6", icon: <FiSend /> },
    { label: "واردة", value: s.received || 0, color: "#10b981", icon: <FiInbox /> },
    { label: "آخر 24 ساعة", value: s.last_24h || 0, color: "#f59e0b", icon: <FiClock /> },
    { label: "آخر 7 أيام", value: s.last_7d || 0, color: "#06b6d4", icon: <FiBarChart2 /> },
    { label: "فشل", value: s.failed || 0, color: "#ef4444", icon: <FiAlertCircle /> },
    { label: "غير مقروءة", value: data.unreadCount || 0, color: "#f97316", icon: <FiEye /> },
  ];

  return (
    <div>
      {/* Stats Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
        {statCards.map((c, i) => (
          <div key={i} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "16px 14px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <div style={{ color: c.color, fontSize: 20 }}>{c.icon}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: c.color }}>{Number(c.value).toLocaleString("en-US")}</div>
            <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Template Usage */}
      {data.templateUsage?.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 20 }}>
          <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700, color: "#374151" }}>أكثر القوالب استخداماً</h4>
          {data.templateUsage.map((t, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: i < data.templateUsage.length - 1 ? "1px solid #f3f4f6" : "none" }}>
              <span style={{ fontSize: 13, color: "#374151" }}>{t.name_ar || t.name}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#3b82f6" }}>{t.usage_count}</span>
            </div>
          ))}
        </div>
      )}

      {/* Recent Activity */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#374151" }}>آخر النشاط</h4>
          <button onClick={loadDashboard} style={{ border: "none", background: "none", cursor: "pointer", color: "#6b7280" }}><FiRefreshCw size={14} /></button>
        </div>
        <div style={{ maxHeight: 300, overflowY: "auto" }}>
          {(data.recentActivity || []).map((m, i) => (
            <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: "1px solid #f3f4f6", fontSize: 12 }}>
              <span style={{ color: m.direction === "inbound" ? "#10b981" : "#3b82f6", fontWeight: 700, minWidth: 50 }}>
                {m.direction === "inbound" ? "وارد" : "صادر"}
              </span>
              <span style={{ color: "#374151", flex: 1 }}>{m.contact_name || formatPhone(m.phone)}</span>
              <span style={{ color: "#9ca3af", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.message_body?.substring(0, 60)}</span>
              <span style={{ color: STATUS_COLORS[m.status], fontWeight: 600, minWidth: 60 }}>{STATUS_LABELS[m.status]}</span>
              <span style={{ color: "#9ca3af" }}>{formatDate(m.created_at)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Messages Tab — سجل الرسائل
// ═══════════════════════════════════════════════════════════════
function MessagesTab() {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ direction: "", status: "", recipient_type: "", search: "", trigger_type: "" });
  const [page, setPage] = useState(0);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, limit: LIMIT, offset: page * LIMIT };
      Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
      const res = await waBusiness.getMessages(params);
      setMessages(res.data.messages || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error("خطأ في تحميل الرسائل");
    }
    setLoading(false);
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ position: "relative", flex: "1 1 200px" }}>
          <FiSearch style={{ position: "absolute", right: 10, top: 9, color: "#9ca3af" }} />
          <input placeholder="بحث..." value={filters.search} onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(0); }}
            style={{ width: "100%", padding: "8px 34px 8px 10px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, direction: "rtl" }} />
        </div>
        <select value={filters.direction} onChange={e => { setFilters(f => ({ ...f, direction: e.target.value })); setPage(0); }}
          style={{ padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, background: "#fff" }}>
          <option value="">كل الاتجاهات</option>
          <option value="outbound">صادر</option>
          <option value="inbound">وارد</option>
        </select>
        <select value={filters.status} onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(0); }}
          style={{ padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, background: "#fff" }}>
          <option value="">كل الحالات</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filters.trigger_type} onChange={e => { setFilters(f => ({ ...f, trigger_type: e.target.value })); setPage(0); }}
          style={{ padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, background: "#fff" }}>
          <option value="">كل الأنواع</option>
          {Object.entries(TRIGGER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={load} style={{ padding: "8px 14px", border: "1px solid #d1d5db", borderRadius: 8, background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 13, color: "#374151" }}>
          <FiRefreshCw size={13} /> تحديث
        </button>
      </div>

      {/* Messages Table */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                <th style={thStyle}>الاتجاه</th>
                <th style={thStyle}>الرقم</th>
                <th style={thStyle}>الاسم</th>
                <th style={thStyle}>النوع</th>
                <th style={thStyle}>الرسالة</th>
                <th style={thStyle}>الحالة</th>
                <th style={thStyle}>المرسل</th>
                <th style={thStyle}>التاريخ</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}>جاري التحميل...</td></tr>
              ) : messages.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}>لا توجد رسائل</td></tr>
              ) : messages.map(m => (
                <tr key={m.id} style={{ borderBottom: "1px solid #f3f4f6", background: m.direction === "inbound" && !m.is_read ? "#fffbeb" : "#fff" }}>
                  <td style={tdStyle}>
                    <span style={{ color: m.direction === "inbound" ? "#10b981" : "#3b82f6", fontWeight: 700, fontSize: 11 }}>
                      {m.direction === "inbound" ? "⬇ وارد" : "⬆ صادر"}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, direction: "ltr", textAlign: "center", fontFamily: "monospace", fontSize: 11 }}>{formatPhone(m.phone)}</td>
                  <td style={tdStyle}>{m.contact_name || "—"}</td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "#f3f4f6", color: "#374151" }}>
                      {TRIGGER_LABELS[m.trigger_type] || m.trigger_type || m.message_type}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {m.template_name && <span style={{ fontSize: 10, color: "#6366f1", marginLeft: 4 }}>[{m.template_name}]</span>}
                    {m.message_body?.substring(0, 80)}
                  </td>
                  <td style={tdStyle}>
                    <span style={{ color: STATUS_COLORS[m.status], fontWeight: 700, fontSize: 11 }}>
                      {STATUS_LABELS[m.status] || m.status}
                    </span>
                    {m.error_message && <div style={{ fontSize: 10, color: "#ef4444" }}>{m.error_message}</div>}
                  </td>
                  <td style={tdStyle}>{m.sent_by_name || "—"}</td>
                  <td style={{ ...tdStyle, fontSize: 11, color: "#9ca3af", whiteSpace: "nowrap" }}>{formatDate(m.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > LIMIT && (
          <div style={{ display: "flex", justifyContent: "center", gap: 8, padding: 12, borderTop: "1px solid #e5e7eb" }}>
            <button disabled={page === 0} onClick={() => setPage(p => p - 1)} style={pgBtnStyle}>السابق</button>
            <span style={{ fontSize: 12, color: "#6b7280", padding: "6px 0" }}>
              {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} من {total}
            </span>
            <button disabled={(page + 1) * LIMIT >= total} onClick={() => setPage(p => p + 1)} style={pgBtnStyle}>التالي</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Send Tab — إرسال رسالة فردية
// ═══════════════════════════════════════════════════════════════
function SendTab() {
  const [sendMode, setSendMode] = useState("template"); // template or text
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [phone, setPhone] = useState("");
  const [messageText, setMessageText] = useState("");
  const [variables, setVariables] = useState([]);
  const [recipientType, setRecipientType] = useState("client");
  const [recipientId, setRecipientId] = useState("");
  const [sending, setSending] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [contacts, setContacts] = useState([]);
  const [showContactPicker, setShowContactPicker] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await waBusiness.getTemplates();
      setTemplates(res.data || []);
    } catch (err) { /* ignore */ }
  };

  const searchContacts = async (query) => {
    if (!query || query.length < 2) { setContacts([]); return; }
    try {
      const res = recipientType === "client"
        ? await waBusiness.getContactClients({ search: query })
        : recipientType === "marketer"
        ? await waBusiness.getContactMarketers({ search: query })
        : await waBusiness.getContactProspects({ search: query });
      setContacts(res.data || []);
    } catch (err) { /* ignore */ }
  };

  const selectContact = (c) => {
    setPhone(c.phone);
    setRecipientId(c.id);
    setContactSearch(c.name);
    setShowContactPicker(false);
    setContacts([]);
  };

  const selectTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    const vars = tpl.variables || [];
    setVariables(vars.map(v => ({ ...v, value: "" })));
  };

  const handleSend = async () => {
    if (!phone) return toast.error("أدخل رقم الهاتف");
    if (sendMode === "template" && !selectedTemplate) return toast.error("اختر قالب");
    if (sendMode === "text" && !messageText.trim()) return toast.error("أدخل نص الرسالة");

    setSending(true);
    try {
      const payload = {
        phone,
        recipient_type: recipientType,
        recipient_id: recipientId || null,
      };

      if (sendMode === "template") {
        payload.template_id = selectedTemplate.id;
        payload.template_name = selectedTemplate.name;
        payload.variables = variables.map(v => v.value);
      } else {
        payload.message_text = messageText;
      }

      await waBusiness.send(payload);
      toast.success("تم إرسال الرسالة بنجاح ✅");
      setMessageText("");
      setPhone("");
      setContactSearch("");
      setRecipientId("");
      setSelectedTemplate(null);
      setVariables([]);
    } catch (err) {
      toast.error(err.response?.data?.error || "فشل الإرسال");
    }
    setSending(false);
  };

  return (
    <div style={{ maxWidth: 700 }}>
      <div style={cardStyle}>
        <h4 style={cardTitleStyle}>إرسال رسالة</h4>

        {/* Recipient Type */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>نوع المستلم</label>
          <div style={{ display: "flex", gap: 8 }}>
            {[["client", "عميل"], ["marketer", "مسوق"], ["prospect", "محتمل"], ["other", "آخر"]].map(([v, l]) => (
              <button key={v} onClick={() => setRecipientType(v)}
                style={{ padding: "6px 16px", borderRadius: 6, border: "1px solid #d1d5db", background: recipientType === v ? "#25D366" : "#fff",
                  color: recipientType === v ? "#fff" : "#374151", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Contact Search */}
        {recipientType !== "other" && (
          <div style={{ marginBottom: 14, position: "relative" }}>
            <label style={labelStyle}>بحث عن {recipientType === "client" ? "عميل" : recipientType === "marketer" ? "مسوق" : "محتمل"}</label>
            <input value={contactSearch} placeholder="اكتب الاسم أو الرقم..."
              onChange={e => { setContactSearch(e.target.value); setShowContactPicker(true); searchContacts(e.target.value); }}
              onFocus={() => setShowContactPicker(true)}
              style={inputStyle} />
            {showContactPicker && contacts.length > 0 && (
              <div style={{ position: "absolute", top: "100%", right: 0, left: 0, background: "#fff", border: "1px solid #d1d5db", borderRadius: 8, maxHeight: 200, overflowY: "auto", zIndex: 10, boxShadow: "0 4px 12px rgba(0,0,0,.1)" }}>
                {contacts.map(c => (
                  <div key={c.id} onClick={() => selectContact(c)}
                    style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f3f4f6", fontSize: 12, display: "flex", justifyContent: "space-between" }}
                    onMouseEnter={e => e.target.style.background = "#f0fdf4"} onMouseLeave={e => e.target.style.background = "#fff"}>
                    <span style={{ fontWeight: 600 }}>{c.name}</span>
                    <span style={{ color: "#9ca3af", direction: "ltr" }}>{c.phone}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Phone */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>رقم الهاتف</label>
          <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="966XXXXXXXXX" dir="ltr"
            style={{ ...inputStyle, direction: "ltr", textAlign: "left" }} />
        </div>

        {/* Send Mode */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>نوع الرسالة</label>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setSendMode("template")}
              style={{ padding: "6px 16px", borderRadius: 6, border: "1px solid #d1d5db", background: sendMode === "template" ? "#3b82f6" : "#fff",
                color: sendMode === "template" ? "#fff" : "#374151", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              <FiFileText style={{ marginLeft: 4 }} /> قالب معتمد
            </button>
            <button onClick={() => setSendMode("text")}
              style={{ padding: "6px 16px", borderRadius: 6, border: "1px solid #d1d5db", background: sendMode === "text" ? "#3b82f6" : "#fff",
                color: sendMode === "text" ? "#fff" : "#374151", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              <FiMessageCircle style={{ marginLeft: 4 }} /> نص حر (24h)
            </button>
          </div>
          {sendMode === "text" && (
            <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 6 }}>⚠ النص الحر يعمل فقط خلال 24 ساعة من آخر رسالة واردة من المستلم</div>
          )}
        </div>

        {/* Template or Text */}
        {sendMode === "template" ? (
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>اختر القالب</label>
            <select value={selectedTemplate?.id || ""} onChange={e => {
              const tpl = templates.find(t => t.id === parseInt(e.target.value));
              if (tpl) selectTemplate(tpl);
            }} style={{ ...inputStyle, cursor: "pointer" }}>
              <option value="">— اختر قالب —</option>
              {templates.filter(t => t.is_active && (t.meta_status === "APPROVED" || t.meta_status === "LOCAL")).map(t => (
                <option key={t.id} value={t.id}>{t.name_ar || t.name} ({t.meta_status})</option>
              ))}
            </select>

            {selectedTemplate && (
              <div style={{ marginTop: 10, padding: 12, background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0" }}>
                <div style={{ fontSize: 12, color: "#166534", fontWeight: 600, marginBottom: 6 }}>معاينة القالب:</div>
                <div style={{ fontSize: 13, color: "#374151", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{selectedTemplate.body_text}</div>
              </div>
            )}

            {/* Variables */}
            {variables.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <label style={labelStyle}>المتغيرات</label>
                {variables.map((v, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: "#6b7280", minWidth: 80 }}>{`{{${v.index || i + 1}}}`} {v.description}</span>
                    <input value={v.value} onChange={e => {
                      const newVars = [...variables];
                      newVars[i] = { ...newVars[i], value: e.target.value };
                      setVariables(newVars);
                    }} placeholder={v.sample_value || "القيمة"} style={{ ...inputStyle, flex: 1 }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>نص الرسالة</label>
            <textarea value={messageText} onChange={e => setMessageText(e.target.value)} rows={4}
              placeholder="اكتب رسالتك هنا..."
              style={{ ...inputStyle, resize: "vertical", minHeight: 80 }} />
          </div>
        )}

        {/* Send Button */}
        <button onClick={handleSend} disabled={sending}
          style={{ width: "100%", padding: "10px 0", background: sending ? "#9ca3af" : "#25D366", color: "#fff",
            border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: sending ? "default" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <FiSend /> {sending ? "جاري الإرسال..." : "إرسال"}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Bulk Send Tab — إرسال جماعي
// ═══════════════════════════════════════════════════════════════
function BulkSendTab() {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [recipientType, setRecipientType] = useState("client");
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const [delayMs, setDelayMs] = useState(1000);

  useEffect(() => { loadTemplates(); }, []);
  useEffect(() => { loadContacts(); }, [recipientType]);

  const loadTemplates = async () => {
    try {
      const res = await waBusiness.getTemplates();
      setTemplates((res.data || []).filter(t => t.is_active && (t.meta_status === "APPROVED" || t.meta_status === "LOCAL")));
    } catch (err) { /* ignore */ }
  };

  const loadContacts = async () => {
    setLoading(true);
    try {
      const res = recipientType === "client"
        ? await waBusiness.getContactClients({ search: searchQ, limit: 500 })
        : recipientType === "marketer"
        ? await waBusiness.getContactMarketers({ search: searchQ, limit: 500 })
        : await waBusiness.getContactProspects({ search: searchQ, limit: 500 });
      setContacts(res.data || []);
      setSelectedContacts([]);
    } catch (err) { /* ignore */ }
    setLoading(false);
  };

  const toggleContact = (c) => {
    setSelectedContacts(prev => {
      const exists = prev.find(x => x.id === c.id);
      return exists ? prev.filter(x => x.id !== c.id) : [...prev, c];
    });
  };

  const toggleAll = () => {
    setSelectedContacts(prev => prev.length === contacts.length ? [] : [...contacts]);
  };

  const handleBulkSend = async () => {
    if (!selectedTemplate) return toast.error("اختر قالب");
    if (selectedContacts.length === 0) return toast.error("اختر مستلمين");

    setSending(true);
    try {
      const recipients = selectedContacts.map(c => ({
        phone: c.phone,
        name: c.name,
        recipient_type: recipientType,
        recipient_id: c.id,
      }));

      const res = await waBusiness.sendBulk({
        recipients,
        template_id: selectedTemplate.id,
        delay_ms: delayMs,
      });

      setBatchId(res.data.batch_id);
      toast.success(`بدأ الإرسال لـ ${recipients.length} مستلم`);
    } catch (err) {
      toast.error(err.response?.data?.error || "فشل الإرسال");
    }
    setSending(false);
  };

  // Poll batch status
  useEffect(() => {
    if (!batchId) return;
    const interval = setInterval(async () => {
      try {
        const res = await waBusiness.getBulkStatus(batchId);
        setBatchStatus(res.data);
        if (parseInt(res.data.total) === parseInt(res.data.sent) + parseInt(res.data.failed)) {
          clearInterval(interval);
        }
      } catch (err) { /* ignore */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [batchId]);

  return (
    <div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        {/* Left: Contact Picker */}
        <div style={{ ...cardStyle, flex: "1 1 400px" }}>
          <h4 style={cardTitleStyle}>اختر المستلمين</h4>

          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <button onClick={() => setRecipientType("client")}
              style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #d1d5db", background: recipientType === "client" ? "#25D366" : "#fff",
                color: recipientType === "client" ? "#fff" : "#374151", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              عملاء
            </button>
            <button onClick={() => setRecipientType("marketer")}
              style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #d1d5db", background: recipientType === "marketer" ? "#25D366" : "#fff",
                color: recipientType === "marketer" ? "#fff" : "#374151", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              مسوقين
            </button>
            <button onClick={() => setRecipientType("prospect")}
              style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #d1d5db", background: recipientType === "prospect" ? "#25D366" : "#fff",
                color: recipientType === "prospect" ? "#fff" : "#374151", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              محتملين
            </button>
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="بحث..." style={{ ...inputStyle, flex: 1 }}
              onKeyDown={e => e.key === "Enter" && loadContacts()} />
            <button onClick={loadContacts} style={btnSecondaryStyle}><FiSearch size={13} /></button>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: "#6b7280" }}>{contacts.length} جهة اتصال — {selectedContacts.length} محدد</span>
            <button onClick={toggleAll} style={{ border: "none", background: "none", color: "#3b82f6", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              {selectedContacts.length === contacts.length ? "إلغاء الكل" : "تحديد الكل"}
            </button>
          </div>

          <div style={{ maxHeight: 350, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
            {loading ? (
              <div style={{ textAlign: "center", padding: 20, color: "#9ca3af" }}>جاري التحميل...</div>
            ) : contacts.map(c => {
              const selected = selectedContacts.find(x => x.id === c.id);
              return (
                <div key={c.id} onClick={() => toggleContact(c)}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", cursor: "pointer",
                    borderBottom: "1px solid #f3f4f6", background: selected ? "#f0fdf4" : "#fff" }}>
                  <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${selected ? "#25D366" : "#d1d5db"}`,
                    background: selected ? "#25D366" : "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {selected && <FiCheck size={12} color="#fff" />}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#374151", flex: 1 }}>{c.name}</span>
                  <span style={{ fontSize: 11, color: "#9ca3af", direction: "ltr" }}>{c.phone}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Template & Send */}
        <div style={{ ...cardStyle, flex: "1 1 300px" }}>
          <h4 style={cardTitleStyle}>إعدادات الإرسال</h4>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>القالب</label>
            <select value={selectedTemplate?.id || ""} onChange={e => {
              const tpl = templates.find(t => t.id === parseInt(e.target.value));
              setSelectedTemplate(tpl || null);
            }} style={{ ...inputStyle, cursor: "pointer" }}>
              <option value="">— اختر قالب —</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name_ar || t.name}</option>)}
            </select>
          </div>

          {selectedTemplate && (
            <div style={{ marginBottom: 14, padding: 10, background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0" }}>
              <div style={{ fontSize: 12, color: "#166534", fontWeight: 600, marginBottom: 4 }}>القالب:</div>
              <div style={{ fontSize: 12, color: "#374151", whiteSpace: "pre-wrap" }}>{selectedTemplate.body_text}</div>
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>التأخير بين الرسائل (مللي ثانية)</label>
            <input type="number" value={delayMs} onChange={e => setDelayMs(parseInt(e.target.value) || 1000)} min={500} max={10000}
              style={{ ...inputStyle, direction: "ltr", textAlign: "center" }} />
          </div>

          <div style={{ marginBottom: 14, padding: 10, background: "#fef3c7", borderRadius: 8 }}>
            <div style={{ fontSize: 12, color: "#92400e" }}>
              <strong>ملخص:</strong> سيتم إرسال {selectedContacts.length} رسالة
              {selectedTemplate && ` باستخدام قالب "${selectedTemplate.name_ar || selectedTemplate.name}"`}
            </div>
          </div>

          <button onClick={handleBulkSend} disabled={sending || selectedContacts.length === 0 || !selectedTemplate}
            style={{ width: "100%", padding: "10px 0", background: sending ? "#9ca3af" : "#25D366", color: "#fff",
              border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: sending ? "default" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <FiSend /> {sending ? "جاري الإرسال..." : `إرسال لـ ${selectedContacts.length} مستلم`}
          </button>

          {/* Batch Status */}
          {batchStatus && (
            <div style={{ marginTop: 14, padding: 10, background: "#f0f9ff", borderRadius: 8, border: "1px solid #bae6fd" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0369a1", marginBottom: 8 }}>حالة الإرسال</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 12 }}>
                <span>الإجمالي: <strong>{batchStatus.total}</strong></span>
                <span>تم الإرسال: <strong style={{ color: "#10b981" }}>{batchStatus.sent}</strong></span>
                <span>تم التوصيل: <strong style={{ color: "#8b5cf6" }}>{batchStatus.delivered}</strong></span>
                <span>فشل: <strong style={{ color: "#ef4444" }}>{batchStatus.failed}</strong></span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Inbox Tab — الوارد
// ═══════════════════════════════════════════════════════════════
function InboxTab() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [conversation, setConversation] = useState([]);

  useEffect(() => { loadInbox(); }, []);

  const loadInbox = async () => {
    setLoading(true);
    try {
      const res = await waBusiness.getMessages({ direction: "inbound", limit: 200 });
      const msgs = res.data.messages || [];
      // Group by phone
      const grouped = {};
      msgs.forEach(m => {
        const key = m.phone?.replace(/\D/g, "");
        if (!grouped[key]) grouped[key] = { phone: m.phone, contact_name: m.contact_name, messages: [], unread: 0, lastMsg: m };
        grouped[key].messages.push(m);
        if (!m.is_read) grouped[key].unread++;
      });
      setMessages(Object.values(grouped).sort((a, b) => new Date(b.lastMsg.created_at) - new Date(a.lastMsg.created_at)));
    } catch (err) {
      toast.error("خطأ في تحميل الوارد");
    }
    setLoading(false);
  };

  const loadConversation = async (phone) => {
    setSelectedPhone(phone);
    try {
      const res = await waBusiness.getConversation(phone.replace(/\D/g, ""));
      setConversation(res.data || []);
      // Mark as read
      for (const m of (res.data || []).filter(m => m.direction === "inbound" && !m.is_read)) {
        await waBusiness.markRead(m.id).catch(() => {});
      }
    } catch (err) { /* ignore */ }
  };

  return (
    <div style={{ display: "flex", gap: 16, height: "calc(100vh - 240px)", minHeight: 400 }}>
      {/* Contact List */}
      <div style={{ ...cardStyle, width: 320, minWidth: 280, overflowY: "auto", padding: 0 }}>
        <div style={{ padding: "12px 14px", borderBottom: "2px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>المحادثات</h4>
          <button onClick={loadInbox} style={{ border: "none", background: "none", cursor: "pointer", color: "#6b7280" }}><FiRefreshCw size={14} /></button>
        </div>
        {loading ? (
          <div style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}>جاري التحميل...</div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}>لا توجد رسائل واردة</div>
        ) : messages.map((g, i) => (
          <div key={i} onClick={() => loadConversation(g.phone)}
            style={{ padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #f3f4f6",
              background: selectedPhone === g.phone ? "#f0fdf4" : "#fff" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>{g.contact_name || formatPhone(g.phone)}</span>
              {g.unread > 0 && (
                <span style={{ background: "#25D366", color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 700 }}>{g.unread}</span>
              )}
            </div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {g.lastMsg.message_body?.substring(0, 50)}
            </div>
            <div style={{ fontSize: 10, color: "#d1d5db", marginTop: 2 }}>{formatDate(g.lastMsg.created_at)}</div>
          </div>
        ))}
      </div>

      {/* Conversation View */}
      <div style={{ ...cardStyle, flex: 1, display: "flex", flexDirection: "column", padding: 0 }}>
        {!selectedPhone ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 14 }}>
            اختر محادثة لعرضها
          </div>
        ) : (
          <>
            <div style={{ padding: "12px 16px", borderBottom: "2px solid #e5e7eb", fontWeight: 700, fontSize: 14, color: "#374151" }}>
              {formatPhone(selectedPhone)}
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
              {conversation.map((m, i) => (
                <div key={i} style={{ alignSelf: m.direction === "outbound" ? "flex-start" : "flex-end", maxWidth: "75%" }}>
                  <div style={{
                    padding: "8px 12px", borderRadius: 10,
                    background: m.direction === "outbound" ? "#dcfce7" : "#fff",
                    border: `1px solid ${m.direction === "outbound" ? "#bbf7d0" : "#e5e7eb"}`,
                  }}>
                    <WaMedia m={m} src={`/api/wa-business/media/${m.id}`} /> {/* v3.4.091 */}
                    <div style={{ fontSize: 13, color: "#374151", whiteSpace: "pre-wrap" }}>{m.media_id && isPlaceholderBody(m.message_body) ? null : m.message_body}</div>
                    <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 4, display: "flex", gap: 8, justifyContent: "space-between" }}>
                      <span>{m.direction === "outbound" ? `⬆ ${m.sent_by_name || ""}` : "⬇ وارد"}</span>
                      <span>{formatDate(m.created_at)}</span>
                      <span style={{ color: STATUS_COLORS[m.status] }}>{STATUS_LABELS[m.status]}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Templates Tab — القوالب
// ═══════════════════════════════════════════════════════════════
function TemplatesTab({ isAdmin }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  // v3.4.098 — القالب يُنشأ في ميتا مباشرةً لحساب الرقم المختار (waba_id) مع نماذج المتغيرات وأزرار الرد السريع
  const [newTpl, setNewTpl] = useState({ name: "", name_ar: "", body_text: "", language: "ar", category: "MARKETING", usage_type: "custom", waba_id: "", samples: [], quick_replies: ["", "", ""], submit: true });
  const [wabas, setWabas] = useState([]);
  const nVars = Math.max(0, ...[...String(newTpl.body_text || "").matchAll(/\{\{(\d+)\}\}/g)].map(m => Number(m[1])));
  const preview = String(newTpl.body_text || "").replace(/\{\{(\d+)\}\}/g, (_, n) => newTpl.samples[Number(n) - 1] || `{{${n}}}`);

  useEffect(() => { loadTemplates(); waBusiness.wabas().then(r => { const w = r.data.wabas || []; setWabas(w); if (w.length && !newTpl.waba_id) setNewTpl(p => ({ ...p, waba_id: p.waba_id || w[0].waba_id })); }).catch(() => {}); }, []); // eslint-disable-line

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await waBusiness.getTemplates();
      setTemplates(res.data || []);
    } catch (err) {
      toast.error("خطأ في تحميل القوالب");
    }
    setLoading(false);
  };

  const syncFromMeta = async () => {
    setSyncing(true);
    try {
      const res = await waBusiness.syncTemplates();
      toast.success(`تمت مزامنة ${res.data.synced} قالب من Meta`);
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.error || "فشل المزامنة");
    }
    setSyncing(false);
  };

  const [adding, setAdding] = useState(false);
  const addTemplate = async () => {
    if (!newTpl.name || !newTpl.body_text) return toast.error("الاسم ونص القالب مطلوبين");
    if (newTpl.submit && !newTpl.waba_id) return toast.error("اختر الحساب/الرقم الذي سيُنشأ له القالب في ميتا");
    setAdding(true);
    try {
      const r = await waBusiness.createTemplate({ ...newTpl, quick_replies: newTpl.quick_replies.filter(Boolean) });
      toast.success(r.data?.meta_status === "LOCAL" ? "حُفظ القالب محليًا" : `أُرسل القالب إلى ميتا للمراجعة (${r.data?.meta_status || "PENDING"}) — اضغط «مزامنة» لاحقًا لتحديث الحالة`);
      setShowAdd(false);
      setNewTpl(p => ({ name: "", name_ar: "", body_text: "", language: "ar", category: "MARKETING", usage_type: "custom", waba_id: p.waba_id, samples: [], quick_replies: ["", "", ""], submit: true }));
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.error || "فشل الإضافة", { duration: 8000 });
    }
    setAdding(false);
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("حذف هذا القالب؟")) return;
    try {
      await waBusiness.deleteTemplate(id);
      toast.success("تم الحذف");
      loadTemplates();
    } catch (err) {
      toast.error("فشل الحذف");
    }
  };

  const toggleActive = async (t) => {
    try {
      await waBusiness.updateTemplate(t.id, { is_active: !t.is_active });
      loadTemplates();
    } catch (err) { /* ignore */ }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button onClick={syncFromMeta} disabled={syncing}
          style={{ ...btnPrimaryStyle, background: syncing ? "#9ca3af" : "#3b82f6" }}>
          <FiRefreshCw size={13} className={syncing ? "spin" : ""} /> {syncing ? "جاري المزامنة..." : "مزامنة من Meta"}
        </button>
        {isAdmin && (
          <button onClick={() => setShowAdd(!showAdd)} style={btnSecondaryStyle}>
            <FiPlus size={13} /> إضافة قالب
          </button>
        )}
      </div>

      {/* Add Form — v3.4.098: يُنشأ في ميتا مباشرةً */}
      {showAdd && (
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <h4 style={cardTitleStyle}>قالب جديد — يُرسل إلى ميتا للاعتماد</h4>
          <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "8px 12px", fontSize: 11.5, color: "#1e40af", marginBottom: 10, lineHeight: 1.7 }}>
            القوالب تتبع حساب واتساب الأعمال الخاص بكل رقم. اختر الرقم، واكتب نصًا واضحًا يذكر اسم الشركة وسبب التواصل (ميتا ترفض الرسائل العامة مثل «أي تحديث؟»)،
            وأضف مثالًا لكل متغير. متابعات مثل «هل ما زلت مهتمًا بالعرض؟» تُصنّف <b>تسويقية</b>. الاعتماد عادةً خلال ساعة، ثم اضغط «مزامنة من Meta».
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <label style={labelStyle}>الحساب / الرقم</label>
              <select value={newTpl.waba_id} onChange={e => setNewTpl(p => ({ ...p, waba_id: e.target.value }))} style={inputStyle}>
                <option value="">— بلا (حفظ محلي فقط) —</option>
                {wabas.map(w => <option key={w.waba_id} value={w.waba_id}>{w.numbers_label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>الاسم التقني (English, a-z_)</label>
              <input value={newTpl.name} onChange={e => setNewTpl(p => ({ ...p, name: e.target.value }))} placeholder="followup_offer_ar" dir="ltr" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>الاسم (عربي — للعرض)</label>
              <input value={newTpl.name_ar} onChange={e => setNewTpl(p => ({ ...p, name_ar: e.target.value }))} placeholder="متابعة العرض" style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>نص القالب</label>
            <textarea value={newTpl.body_text} onChange={e => setNewTpl(p => ({ ...p, body_text: e.target.value }))} rows={4}
              placeholder="مرحبًا {{1}}، معك {{2}} من أصول للتطوير العمراني. أتابع معك بخصوص عرض الشقة الذي تحدثنا عنه، هل لديك أي تحديث؟" style={{ ...inputStyle, resize: "vertical" }} />
            <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 4 }}>استخدم {"{{1}}"} {"{{2}}"} ... للمتغيرات — لا تبدأ النص أو تنهِه بمتغير</div>
          </div>
          {nVars > 0 && (
            <div style={{ marginBottom: 10 }}>
              <label style={labelStyle}>أمثلة المتغيرات (تطلبها ميتا للمراجعة)</label>
              <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(nVars, 3)}, 1fr)`, gap: 8 }}>
                {Array.from({ length: nVars }, (_, i) => (
                  <input key={i} value={newTpl.samples[i] || ""} placeholder={i === 0 ? "مثال: محمد" : `مثال للمتغير ${i + 1}`}
                    onChange={e => setNewTpl(p => { const s = [...p.samples]; s[i] = e.target.value; return { ...p, samples: s }; })} style={inputStyle} />
                ))}
              </div>
              <div style={{ fontSize: 11.5, color: "#374151", background: "#f9fafb", border: "1px dashed #e5e7eb", borderRadius: 8, padding: "8px 10px", marginTop: 8, whiteSpace: "pre-wrap" }}>معاينة: {preview}</div>
            </div>
          )}
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>أزرار رد سريع (اختياري، حتى 3) — ضغطة العميل عليها تفتح نافذة الرد الحر 24 ساعة</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {[0, 1, 2].map(i => (
                <input key={i} value={newTpl.quick_replies[i] || ""} placeholder={["نعم، مهتم", "غير مهتم", "تواصلوا معي لاحقًا"][i]} maxLength={25}
                  onChange={e => setNewTpl(p => { const q = [...p.quick_replies]; q[i] = e.target.value; return { ...p, quick_replies: q }; })} style={inputStyle} />
              ))}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>اللغة</label>
              <select value={newTpl.language} onChange={e => setNewTpl(p => ({ ...p, language: e.target.value }))} style={inputStyle}>
                <option value="ar">العربية</option>
                <option value="en">English</option>
                <option value="en_US">English (US)</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>الفئة</label>
              <select value={newTpl.category} onChange={e => setNewTpl(p => ({ ...p, category: e.target.value }))} style={inputStyle}>
                <option value="MARKETING">تسويقية (Marketing) — متابعات وعروض</option>
                <option value="UTILITY">خدمية (Utility) — تحديثات على طلب قائم</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>الاستخدام</label>
              <select value={newTpl.usage_type} onChange={e => setNewTpl(p => ({ ...p, usage_type: e.target.value }))} style={inputStyle}>
                <option value="custom">عام</option>
                <option value="reservation_confirm">تأكيد حجز</option>
                <option value="payment_reminder">تذكير دفعة</option>
                <option value="project_update">تحديث مشروع</option>
              </select>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
            <label style={{ fontSize: 11.5, color: "#374151", display: "flex", alignItems: "center", gap: 6, marginInlineEnd: "auto" }}>
              <input type="checkbox" checked={newTpl.submit} onChange={e => setNewTpl(p => ({ ...p, submit: e.target.checked }))} /> إرسال إلى ميتا للاعتماد (وإلا يُحفظ محليًا فقط)
            </label>
            <button onClick={() => setShowAdd(false)} style={btnSecondaryStyle}>إلغاء</button>
            <button onClick={addTemplate} disabled={adding} style={{ ...btnPrimaryStyle, opacity: adding ? .6 : 1 }}><FiCheck size={13} /> {adding ? "جارٍ الإرسال…" : (newTpl.submit && newTpl.waba_id ? "إنشاء في ميتا" : "حفظ محليًا")}</button>
          </div>
        </div>
      )}

      {/* Templates List */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 12 }}>
        {loading ? (
          <div style={{ gridColumn: "1/-1", textAlign: "center", padding: 30, color: "#9ca3af" }}>جاري التحميل...</div>
        ) : templates.length === 0 ? (
          <div style={{ gridColumn: "1/-1", textAlign: "center", padding: 30, color: "#9ca3af" }}>لا توجد قوالب — اضغط "مزامنة من Meta" لجلب القوالب</div>
        ) : templates.map(t => (
          <div key={t.id} style={{ ...cardStyle, opacity: t.is_active ? 1 : 0.5, position: "relative" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#374151" }}>{t.name_ar || t.name}</div>
                {t.name_ar && <div style={{ fontSize: 11, color: "#9ca3af" }}>{t.name}</div>}
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, fontWeight: 600,
                  background: t.meta_status === "APPROVED" ? "#dcfce7" : t.meta_status === "LOCAL" ? "#dbeafe" : "#fef3c7",
                  color: t.meta_status === "APPROVED" ? "#166534" : t.meta_status === "LOCAL" ? "#1e40af" : "#92400e" }}>
                  {t.meta_status}
                </span>
                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: "#f3f4f6", color: "#6b7280" }}>
                  {t.category}
                </span>
              </div>
            </div>
            {(t.numbers_label || t.waba_id) && <div style={{ fontSize: 10.5, color: "#0f766e", marginBottom: 6 }}>☎ {t.numbers_label || `حساب ${t.waba_id}`}{t.language ? ` · ${t.language}` : ""}</div>}{/* v3.4.098 */}
            <div style={{ fontSize: 12, color: "#6b7280", whiteSpace: "pre-wrap", lineHeight: 1.6, marginBottom: 10, maxHeight: 100, overflow: "hidden" }}>
              {t.body_text}
            </div>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <button onClick={() => toggleActive(t)} style={{ border: "none", background: "none", cursor: "pointer", color: t.is_active ? "#f59e0b" : "#10b981", fontSize: 11 }}>
                {t.is_active ? "تعطيل" : "تفعيل"}
              </button>
              {isAdmin && (
                <button onClick={() => deleteTemplate(t.id)} style={{ border: "none", background: "none", cursor: "pointer", color: "#ef4444", fontSize: 11 }}>
                  <FiTrash2 size={12} /> حذف
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Auto Notifications Tab — الإشعارات التلقائية
// ═══════════════════════════════════════════════════════════════
function AutoNotificationsTab() {
  const [notifications, setNotifications] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newNotif, setNewNotif] = useState({ trigger_event: "reservation_created", template_id: "", target_type: "client", delay_minutes: 0 });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [nRes, tRes] = await Promise.all([
        waBusiness.getAutoNotifications(),
        waBusiness.getTemplates(),
      ]);
      setNotifications(nRes.data || []);
      setTemplates((tRes.data || []).filter(t => t.is_active));
    } catch (err) {
      toast.error("خطأ في التحميل");
    }
    setLoading(false);
  };

  const addNotif = async () => {
    if (!newNotif.template_id) return toast.error("اختر قالب");
    try {
      await waBusiness.createAutoNotification(newNotif);
      toast.success("تم إضافة الإشعار التلقائي");
      setShowAdd(false);
      loadAll();
    } catch (err) {
      toast.error("فشل الإضافة");
    }
  };

  const toggleNotif = async (n) => {
    try {
      await waBusiness.updateAutoNotification(n.id, { is_active: !n.is_active });
      loadAll();
    } catch (err) { /* ignore */ }
  };

  const deleteNotif = async (id) => {
    if (!window.confirm("حذف هذا الإشعار؟")) return;
    try {
      await waBusiness.deleteAutoNotification(id);
      toast.success("تم الحذف");
      loadAll();
    } catch (err) { /* ignore */ }
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => setShowAdd(!showAdd)} style={btnPrimaryStyle}>
          <FiPlus size={13} /> إضافة إشعار تلقائي
        </button>
      </div>

      {showAdd && (
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <h4 style={cardTitleStyle}>إشعار تلقائي جديد</h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <label style={labelStyle}>الحدث المُفعّل</label>
              <select value={newNotif.trigger_event} onChange={e => setNewNotif(p => ({ ...p, trigger_event: e.target.value }))} style={inputStyle}>
                {TRIGGER_EVENTS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>القالب</label>
              <select value={newNotif.template_id} onChange={e => setNewNotif(p => ({ ...p, template_id: parseInt(e.target.value) }))} style={inputStyle}>
                <option value="">— اختر —</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name_ar || t.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>المستهدف</label>
              <select value={newNotif.target_type} onChange={e => setNewNotif(p => ({ ...p, target_type: e.target.value }))} style={inputStyle}>
                <option value="client">العميل</option>
                <option value="marketer">المسوق</option>
                <option value="both">كلاهما</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>التأخير (دقائق)</label>
              <input type="number" value={newNotif.delay_minutes} onChange={e => setNewNotif(p => ({ ...p, delay_minutes: parseInt(e.target.value) || 0 }))}
                min={0} style={{ ...inputStyle, direction: "ltr", textAlign: "center" }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={() => setShowAdd(false)} style={btnSecondaryStyle}>إلغاء</button>
            <button onClick={addNotif} style={btnPrimaryStyle}><FiCheck size={13} /> حفظ</button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}>جاري التحميل...</div>
      ) : notifications.length === 0 ? (
        <div style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}>لا توجد إشعارات تلقائية</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
          {notifications.map(n => (
            <div key={n.id} style={{ ...cardStyle, opacity: n.is_active ? 1 : 0.5 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#374151" }}>
                  <FiZap style={{ color: "#f59e0b", marginLeft: 4 }} />
                  {TRIGGER_EVENTS.find(e => e.value === n.trigger_event)?.label || n.trigger_event}
                </div>
                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, fontWeight: 600,
                  background: n.is_active ? "#dcfce7" : "#fee2e2",
                  color: n.is_active ? "#166534" : "#991b1b" }}>
                  {n.is_active ? "مفعّل" : "معطّل"}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>القالب: {n.template_name_ar || n.template_name || "—"}</div>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>المستهدف: {n.target_type === "both" ? "العميل + المسوق" : n.target_type === "client" ? "العميل" : "المسوق"}</div>
              {n.delay_minutes > 0 && <div style={{ fontSize: 12, color: "#6b7280" }}>تأخير: {n.delay_minutes} دقيقة</div>}
              <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 8 }}>
                <button onClick={() => toggleNotif(n)} style={{ border: "none", background: "none", cursor: "pointer", color: n.is_active ? "#f59e0b" : "#10b981", fontSize: 11 }}>
                  {n.is_active ? "تعطيل" : "تفعيل"}
                </button>
                <button onClick={() => deleteNotif(n.id)} style={{ border: "none", background: "none", cursor: "pointer", color: "#ef4444", fontSize: 11 }}>
                  <FiTrash2 size={12} /> حذف
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Config Tab — الإعدادات
// ═══════════════════════════════════════════════════════════════
function ConfigTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await waBusiness.getConfigStatus();
      setConfig(res.data);
    } catch (err) {
      toast.error("خطأ في تحميل الإعدادات");
    }
    setLoading(false);
  };

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>جاري التحميل...</div>;

  const checks = [
    { label: "WA_BUSINESS_TOKEN", ok: config?.hasToken },
    { label: "WA_PHONE_NUMBER_ID", ok: config?.hasPhoneId },
    { label: "WA_BUSINESS_ACCOUNT_ID", ok: config?.hasBusinessId },
    { label: "WA_WEBHOOK_VERIFY_TOKEN", ok: config?.hasWebhookToken },
  ];

  return (
    <div style={{ maxWidth: 600 }}>
      <div style={cardStyle}>
        <h4 style={cardTitleStyle}>حالة الإعدادات</h4>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: config?.configured ? "#10b981" : "#ef4444" }}>
            {config?.configured ? "✅ WhatsApp Business API مُعدّ ومتصل" : "❌ WhatsApp Business API غير مُعدّ"}
          </div>

          {checks.map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid #f3f4f6" }}>
              {c.ok ? <FiCheckCircle color="#10b981" /> : <FiAlertCircle color="#ef4444" />}
              <span style={{ fontSize: 13, color: "#374151", fontFamily: "monospace" }}>{c.label}</span>
              <span style={{ fontSize: 11, color: c.ok ? "#10b981" : "#ef4444", fontWeight: 600, marginRight: "auto" }}>
                {c.ok ? "مُعدّ" : "غير مُعدّ"}
              </span>
            </div>
          ))}
        </div>

        {config?.phoneInfo && !config.phoneInfo.error && (
          <div style={{ padding: 12, background: "#f0fdf4", borderRadius: 8, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#166534", marginBottom: 6 }}>معلومات الرقم</div>
            <div style={{ fontSize: 12, color: "#374151" }}>
              <div>الرقم: <strong dir="ltr">{config.phoneInfo.display_phone_number || "—"}</strong></div>
              <div>الاسم: <strong>{config.phoneInfo.verified_name || "—"}</strong></div>
              <div>الجودة: <strong>{config.phoneInfo.quality_rating || "—"}</strong></div>
            </div>
          </div>
        )}

        <div style={{ padding: 12, background: "#fef3c7", borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>كيفية الإعداد</div>
          <div style={{ fontSize: 12, color: "#78350f", lineHeight: 1.8 }}>
            1. أنشئ تطبيق في Meta Business Suite<br />
            2. فعّل WhatsApp Business API<br />
            3. أضف المتغيرات البيئية في DigitalOcean App Platform:<br />
            <code style={{ fontSize: 11, background: "#fde68a", padding: "1px 4px", borderRadius: 3 }}>WA_BUSINESS_TOKEN</code> — Access Token<br />
            <code style={{ fontSize: 11, background: "#fde68a", padding: "1px 4px", borderRadius: 3 }}>WA_PHONE_NUMBER_ID</code> — Phone Number ID<br />
            <code style={{ fontSize: 11, background: "#fde68a", padding: "1px 4px", borderRadius: 3 }}>WA_BUSINESS_ACCOUNT_ID</code> — Business Account ID<br />
            <code style={{ fontSize: 11, background: "#fde68a", padding: "1px 4px", borderRadius: 3 }}>WA_WEBHOOK_VERIFY_TOKEN</code> — أي قيمة عشوائية<br />
            4. سجّل Webhook URL في Meta:<br />
            <code style={{ fontSize: 11, background: "#fde68a", padding: "1px 4px", borderRadius: 3 }}>
              https://reservation-system-prhq7.ondigitalocean.app/api/wa-business/webhook
            </code>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Shared Styles
// ═══════════════════════════════════════════════════════════════
const thStyle = { padding: "10px 8px", textAlign: "right", fontWeight: 700, color: "#374151", fontSize: 12, whiteSpace: "nowrap" };
const tdStyle = { padding: "8px 8px", color: "#374151", fontSize: 12 };
const pgBtnStyle = { padding: "5px 14px", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 12 };
const cardStyle = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16 };
const cardTitleStyle = { margin: "0 0 14px", fontSize: 15, fontWeight: 700, color: "#374151" };
const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 4 };
const inputStyle = { width: "100%", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, direction: "rtl", boxSizing: "border-box" };
const btnPrimaryStyle = { padding: "8px 16px", border: "none", borderRadius: 8, background: "#25D366", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 };
const btnSecondaryStyle = { padding: "8px 16px", border: "1px solid #d1d5db", borderRadius: 8, background: "#fff", color: "#374151", cursor: "pointer", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 };
